import { CloudArrowDownIcon } from "@heroicons/react/24/outline";
import { SalesFilter, Shimmers, TableComponent } from "components";
import { Action } from "components/buttons/action-button";
import config from "config";
import lodash from "lodash";
import moment from "moment";
import numeral from "numeral";
import { FC, useEffect, useMemo } from "react";
import toast from "react-hot-toast";
import { useNavigate, useSearch } from "react-location";
import { LocationGenerics } from "router/location";
import { getPeriod, useDownloadFile, useTableData, wrapClick } from "utils";
import { getCustomerTypeServiceClasses } from "../../utils/get-customer-type";
import { usePaymentsReceived } from "./hooks";
const DATE_FORMAT = "YYYY-MM-DD";

const Headers = [{ label: "Amount (GHS)", key: "amount", format: "0,0.00" }];

export const PaymentsReceivedPage: FC = () => {
  const dispatchAction =
    (id: string, action: Exclude<Action, "expand" | "goto" | "clone">) =>
    () => {
      navigate({
        search: (old) => ({
          ...old,
          id,
          modal: action,
        }),
      });
    };

  const searchParams = useSearch<LocationGenerics>();
  const navigate = useNavigate<LocationGenerics>();
  const { downloadAction, downloadLoading } = useDownloadFile({
    onError: () => {
      toast(
        JSON.stringify({
          type: "error",
          title: "An error occurred downloading file",
        }),
      );
    },
    getFileName: () => new Date().toISOString() + "_Payments_Received.xlsx",
  });
  const handleDownloadItem = () => {
    const month = lodash.padStart(
      (
        moment(searchParams.fromDate || new Date(), DATE_FORMAT).month() + 1
      ).toString(),
      2,
      "00",
    );
    const year = moment(
      searchParams.fromDate || new Date(),
      DATE_FORMAT,
    ).year();
    downloadAction(
      `https://subs-production.s3.us-west-2.amazonaws.com/sales/${year}-${month}-Payments-Received.xlsx`,
    );
  };

  const filter = useMemo(
    () => ({
      page: searchParams.page || 1,
      pageSize: searchParams.pageSize || 12,
      sort: searchParams.sort || "",
      region: searchParams.region || undefined,
      district: searchParams.district || undefined,
      month:
        moment(searchParams.fromDate || new Date(), DATE_FORMAT).month() + 1,
      year: moment(searchParams.fromDate || new Date(), DATE_FORMAT).year(),
      ...searchParams?.filter,
      serviceClasses:
        getCustomerTypeServiceClasses(searchParams?.filter?.customerTypes) ||
        searchParams?.filter?.serviceClasses,
      fileName: `Customer Report For ${getPeriod(searchParams)}`,
    }),
    [searchParams],
  );

  const { rows, count, loading, refetch, networkStatus } =
    usePaymentsReceived(filter);

  const records = useTableData({ rows, count });

  const onSearch = (searchValue: string, searchField: string) => {
    refetch({
      page: searchParams.page || 1,
      pageSize: searchParams.pageSize || 12,
      sort: searchParams.sort || "",
      region: searchParams.region || undefined,
      district: searchParams.district || undefined,
      month:
        moment(searchParams.fromDate || new Date(), DATE_FORMAT).month() + 1,
      year: moment(searchParams.fromDate || new Date(), DATE_FORMAT).year(),
      ...searchParams?.filter,
      serviceClasses:
        getCustomerTypeServiceClasses(searchParams?.filter?.customerTypes) ||
        searchParams?.filter?.serviceClasses,
      fileName: `Customer Report For ${getPeriod(searchParams)}`,
    });
  };

  useEffect(() => {
    navigate({
      search: (old) => ({
        ...old,
        ...(!old?.page ? { page: config.constants.page } : {}),
        ...(!old?.pageSize ? { pageSize: 100 } : {}),
        ...(!old?.search ? { search: "" } : {}),
        ...(!old?.sort ? { sort: "" } : {}),
      }),
    });
  }, [navigate]);

  return (
    <>
      <div className="flex-1 flex flex-col mx-auto min-w-0 min-h-full overflow-hidden">
        <SalesFilter
          filters={["serviceClasses", "tariffClasses", "customerTypes"]}
        />
        <div className="flex-1 min-w-0  py-4 sm:py-6 overflow-hidden overflow-y-auto light flex">
          <TableComponent
            title={"sales"}
            refetch={refetch}
            onSearchSubmit={onSearch}
            isRefetching={loading && networkStatus === 4}
            loading={loading && ![4, 6].includes(networkStatus)}
            data={records}
            hasSearch={false}
            renderHeaderItems={() => (
              <div className="mt-4 sm:mt-0.5 sm:ml-16 sm:flex space-x-2">
                <button
                  type="button"
                  onClick={wrapClick(handleDownloadItem)}
                  disabled={downloadLoading}
                  className="ml-3 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm leading-4 text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                >
                  <CloudArrowDownIcon
                    className="-ml-0.5 mr-2 h-4 w-4"
                    aria-hidden="true"
                  />
                  {downloadLoading ? "Exporting" : "Export To Excel"}
                </button>
              </div>
            )}
            renderColumns={() => (
              <tr>
                {!searchParams.region && (
                  <th
                    scope="col"
                    className="sticky top-0 border-y border-gray-200 print:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-3 text-left text-xs font-light print:font-bold  text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                  >
                    Region
                  </th>
                )}
                {!searchParams.district && (
                  <th
                    scope="col"
                    className="sticky top-0 border-y border-gray-200 print:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-3 text-left text-xs font-light print:font-bold  text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                  >
                    District
                  </th>
                )}
                <th
                  scope="col"
                  className="sticky top-0 border-y border-gray-200 print:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-3 text-left text-xs font-light print:font-bold  text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                >
                  SPN
                </th>
                <th
                  scope="col"
                  className="sticky top-0 border-y border-gray-200 print:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-3 text-left text-xs font-light print:font-bold  text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                >
                  Account No
                </th>
                <th
                  scope="col"
                  className="sticky top-0 border-y border-gray-200 print:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-3 text-left text-xs font-light print:font-bold  text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                >
                  Meter No
                </th>
                <th
                  scope="col"
                  className="sticky top-0 border-y border-gray-200 print:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-3 text-left text-xs font-light print:font-bold  text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                >
                  Customer Name
                </th>
                <th
                  scope="col"
                  className="sticky top-0 border-y border-gray-200 print:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-3 text-left text-xs font-light print:font-bold  text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                >
                  Origin
                </th>
                <th
                  scope="col"
                  className="sticky top-0 border-y border-gray-200 print:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-3 text-left text-xs font-light print:font-bold  text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                >
                  Type
                </th>
                <th
                  scope="col"
                  className="sticky top-0 border-y border-gray-200 print:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-3 text-left text-xs font-light print:font-bold  text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                >
                  Sub Type
                </th>
                <th
                  scope="col"
                  className="sticky top-0 border-y border-gray-200 print:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-3 text-left text-xs font-light print:font-bold  text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                >
                  Transaction ID
                </th>
                <th
                  scope="col"
                  className="sticky top-0 border-y border-gray-200 print:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-3 text-left text-xs font-light print:font-bold  text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                >
                  Payment Date
                </th>
                <th
                  scope="col"
                  className="sticky top-0 border-y border-gray-200 print:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-3 text-left text-xs font-light print:font-bold  text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                >
                  Received Date
                </th>
                <th
                  scope="col"
                  className="sticky top-0 border-y border-gray-200 print:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-3 text-left text-xs font-light print:font-bold  text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                >
                  Status
                </th>
                <th
                  scope="col"
                  className="sticky top-0 border-y border-gray-200 print:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-3 text-left text-xs font-light print:font-bold  text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                >
                  Processed Date
                </th>
                {Headers.map((header) => (
                  <th
                    key={header.key}
                    scope="col"
                    className="sticky top-0 border-y border-gray-200 print:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-3 text-right text-xs font-light print:font-bold  text-gray-900 dark:text-gray-100 uppercase tracking-wider whitespace-nowrap"
                  >
                    {header.label}
                  </th>
                ))}
              </tr>
            )}
            renderLoader={() => (
              <tr>
                {!searchParams.region && (
                  <td className="px-3 py-2 border-b border-gray-200 print:border-gray-700">
                    <Shimmers.SingleShimmer />
                  </td>
                )}
                {!searchParams.district && (
                  <td className="px-3 py-2 border-b border-gray-200 print:border-gray-700">
                    <Shimmers.SingleShimmer />
                  </td>
                )}
                {lodash.times(Headers.length + 12, (id) => (
                  <td
                    key={id}
                    className="px-3 py-2 border-b border-gray-200 print:border-gray-700"
                  >
                    <Shimmers.SingleShimmer />
                  </td>
                ))}
              </tr>
            )}
            renderItem={(item) => (
              <tr
                key={item._id}
                className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                onClick={wrapClick(dispatchAction(item._id, "view"))}
              >
                {!searchParams.region && (
                  <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-900 border-b border-gray-200 print:border-gray-700 ">
                    {item?.regionName || "N/A"}
                  </td>
                )}
                {!searchParams.district && (
                  <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700 border-b border-gray-200 print:border-gray-700">
                    {item?.districtName || "N/A"}
                  </td>
                )}
                <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700 border-b border-gray-200 print:border-gray-700">
                  {item?.servicePointCode || "N/A"}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700 border-b border-gray-200 print:border-gray-700">
                  {item?.accountCode || "N/A"}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700 border-b border-gray-200 print:border-gray-700">
                  {item?.meterCode || "N/A"}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700 border-b border-gray-200 print:border-gray-700">
                  {item?.customerName || "N/A"}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700 border-b border-gray-200 print:border-gray-700">
                  {item?.origin || "N/A"}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700 border-b border-gray-200 print:border-gray-700">
                  {item?.type || "N/A"}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700 border-b border-gray-200 print:border-gray-700">
                  {item?.subType || "N/A"}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700 border-b border-gray-200 print:border-gray-700">
                  {item?.externalTransactionCode || "N/A"}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700 border-b border-gray-200 print:border-gray-700">
                  {item?.paymentDate
                    ? moment(item.paymentDate).format("DD/MM/YYYY hh:mm A")
                    : "N/A"}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700 border-b border-gray-200 print:border-gray-700">
                  {item?.receivedDate
                    ? moment(item.receivedDate).format("DD/MM/YYYY hh:mm A")
                    : "N/A"}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700 border-b border-gray-200 print:border-gray-700">
                  {item?.status || "N/A"}
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-700 border-b border-gray-200 print:border-gray-700">
                  {item?.processedDate
                    ? moment(item.processedDate).format("DD/MM/YYYY hh:mm A")
                    : "N/A"}
                </td>
                {Headers.map((header) => (
                  <td className="whitespace-nowrap px-3 py-2 text-normal text-right text-gray-700 border-b border-gray-200 print:border-gray-700">
                    {item._id === "space"
                      ? ""
                      : numeral(lodash.get(item, header.key, 0)).format(
                          header.format,
                        ) || "N/A"}
                  </td>
                ))}
              </tr>
            )}
          />
        </div>
      </div>
    </>
  );
};
